import { useState } from "react";
import { ChartCard, type ChartCardState } from "../../ChartCard";
import { useInstanceSnapshots } from "../../../../hooks/useInstanceSnapshots";
import type { Snapshot } from "../../../../hooks/useAdminStatus";
import { formatRelativeTime, formatDateTime } from "../../../../utils/formatDateTime";
import styles from "./SnapshotsPanel.module.css";

type SnapshotsPanelProps = {
  readonly tenantId: string;
};

const TIER_LABELS: Readonly<Record<string, string>> = {
  "ztc-dlm-6h": "Hourly",
  "ztc-dlm-daily": "Daily",
  "ztc-dlm-weekly": "Weekly",
};

const COLLAPSED_ROWS = 6;

const tierLabel = (type: string): string => TIER_LABELS[type] ?? type;

// "4h ago · 10 hourly, 6 daily, 2 weekly", in the order the tiers first appear.
const summary = (rows: ReadonlyArray<Snapshot>): string | undefined => {
  if (rows.length === 0) return undefined;
  const counts = new Map<string, number>();
  for (const row of rows) counts.set(tierLabel(row.type), (counts.get(tierLabel(row.type)) ?? 0) + 1);
  const tiers = [...counts].map(([tier, n]) => `${n} ${tier.toLowerCase()}`).join(", ");
  return `Latest ${formatRelativeTime(rows[0]?.startedAt)} · ${tiers}`;
};

const cardState = (
  status: "loading" | "ready" | "error",
  snapshots: ReadonlyArray<Snapshot>,
): ChartCardState => {
  if (status === "loading") return "loading";
  if (status === "error") return "error";
  if (snapshots.length === 0) return "empty";
  return "ready";
};

export const SnapshotsPanel = ({ tenantId }: SnapshotsPanelProps): JSX.Element => {
  const { status, snapshots, error } = useInstanceSnapshots(tenantId);
  const [expanded, setExpanded] = useState(false);
  const rows = snapshots?.snapshots ?? [];
  const shown = expanded ? rows : rows.slice(0, COLLAPSED_ROWS);
  return (
    <ChartCard
      title={`Restore points${rows.length > 0 ? ` (${rows.length})` : ""}`}
      subtitle={summary(rows)}
      hint="EBS snapshots kept by the backup schedule. Not affected by the time range."
      wide
      state={cardState(status, rows)}
      emptyText="No restore points yet."
      errorText={error ?? undefined}
    >
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Created</th>
            <th>Tier</th>
            <th className={styles.num}>Size</th>
            <th>State</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((s) => (
            <tr key={s.snapshotId}>
              <td title={formatDateTime(s.startedAt)}>{formatRelativeTime(s.startedAt)}</td>
              <td>{tierLabel(s.type)}</td>
              <td className={styles.num}>{s.sizeGiB} GiB</td>
              <td className={s.state === "completed" ? styles.ok : styles.pending}>{s.state}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > COLLAPSED_ROWS && (
        <button type="button" className={styles.more} onClick={() => setExpanded((v) => !v)}>
          {expanded ? "Show fewer" : `Show all ${rows.length}`}
        </button>
      )}
    </ChartCard>
  );
};
